import { useState } from "react";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { safeStorage } from "../utils/safeStorage.js";
import MpxButton from "./MpxButton.jsx";

// Report only the error class and compiled asset locations. Error messages,
// page URLs, account details, query strings and application state stay private.
export function rootErrorDiagnostic(error) {
  const name = ["TypeError", "ReferenceError", "RangeError", "SyntaxError", "Error"].includes(error?.name)
    ? error.name : "Error";
  const frames = [...String(error?.stack || "").split("\n").slice(1).join("\n").matchAll(/\/assets\/[a-zA-Z0-9_.-]+\.js:\d+:\d+/g)]
    .slice(0, 8).map(match => match[0]);
  return [name, ...new Set(frames)].join("\n");
}

export default function RootErrorRecovery({ error, embedded = false, onRetry, onSignOut }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const isChunk = !!error?.isChunkError;
  const isShare = /^\/client\//.test(window.location.pathname);
  const diagnostic = rootErrorDiagnostic(error);
  const Container = embedded ? "section" : "main";
  const signOut = async () => {
    if (busy) return;
    setBusy(true);
    setNotice("");
    if (onSignOut) {
      try { onSignOut(); return; } catch { /* Blocked storage: use durable/server fallback below. */ }
    }
    // AuthProvider is unmounted by the outer boundary. Keep its existing
    // explicit-logout marker and server revocation contract here as well.
    safeStorage.setItem("mediaplex_explicit_logout", "1");
    safeStorage.removeItem("mediaplex_auth_credential");
    let persisted = false;
    try { persisted = localStorage.getItem("mediaplex_explicit_logout") === "1"; } catch { /* blocked storage */ }
    let revoked = false;
    try {
      const response = await fetch("/api/session", {
        method: "DELETE", credentials: "same-origin", keepalive: true,
        signal: AbortSignal.timeout(8000),
      });
      revoked = response.ok;
    } catch { /* The explicit local marker still prevents silent restoration. */ }
    if (persisted || revoked) {
      try { window.google?.accounts?.id?.disableAutoSelect(); } catch { /* optional GIS */ }
      window.location.assign("/login");
    } else {
      setNotice("로그아웃 상태를 확인하지 못했습니다. 연결을 확인한 뒤 다시 시도해 주세요.");
      setBusy(false);
    }
  };
  return (
    <Container aria-label={embedded ? "영역 오류 복구" : "화면 오류 복구"}
      className={`${embedded ? "min-h-[300px]" : "min-h-screen"} flex items-center justify-center p-5`}
      style={{ background: "var(--mpx-bg)", color: "var(--mpx-text-primary)" }}>
      <div className="w-full max-w-md text-center">
        <div className="mb-4 flex justify-center">
          {isChunk ? <RefreshCw size={40} strokeWidth={1.75} aria-hidden="true" />
            : <TriangleAlert size={40} strokeWidth={1.75} aria-hidden="true" />}
        </div>
        <h1 className="text-lg font-semibold mb-2">{isChunk ? "새 버전이 배포되었습니다" : "문제가 발생했습니다"}</h1>
        <p className="text-sm mb-5" style={{ color: "var(--mpx-text-secondary)" }}>
          {isChunk ? "새로고침하면 최신 버전으로 이어서 사용할 수 있습니다."
            : "화면을 불러오지 못했습니다. 새로고침해도 같으면 진단 정보를 전달해 주세요."}
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <MpxButton type="button" disabled={busy} onClick={!isChunk && onRetry ? onRetry : () => window.location.reload()}>
            {!isChunk && onRetry ? "다시 시도" : "새로고침"}
          </MpxButton>
          {embedded && !isShare && <a className="mpx-btn mpx-btn--secondary mpx-btn--md" href="/">워크스페이스 홈</a>}
          {isShare ? <a className="mpx-btn mpx-btn--secondary mpx-btn--md" href="/">MEDIAPLEX 홈</a>
            : <MpxButton type="button" variant="secondary" loading={busy} onClick={signOut}>
              {busy ? "로그아웃 중..." : "로그아웃 / 계정 변경"}
            </MpxButton>}
        </div>
        {notice && <p role="status" className="text-sm mt-4">{notice}</p>}
        <details className="mt-6 text-left text-xs">
          <summary className="cursor-pointer py-2">진단 정보 보기</summary>
          <p className="my-2" style={{ color: "var(--mpx-text-secondary)" }}>아래 오류 종류와 코드 위치를 복사해 전달해 주세요.</p>
          <pre className="p-3 rounded overflow-x-auto select-text" style={{ background: "var(--mpx-surface-secondary)" }}>{diagnostic}</pre>
        </details>
      </div>
    </Container>
  );
}
